import { matchPath } from 'react-router-dom';
import { ToBeDone } from '../db/types';
import { EmptyObject } from '../utils/objects';

// Utility type to match enums wrapped in round brackets
type ExtractEnum<Path extends string> = Path extends `${infer Before}(${infer EnumValues})${infer After}`
  ? EnumValues
  : never;

// Type to parse enum values from a template literal string
type ParseEnumValues<EnumValues extends string> = EnumValues extends `${infer First}|${infer Rest}`
  ? First | ParseEnumValues<Rest>
  : EnumValues;

type ParsedValues = ParseEnumValues<`${ToBeDone.IN_CLASS}|${ToBeDone.PRE_CLASS}`>;

/**
 * Splits input Path on `/` and returns a union of segments.
 * It uses {@link https://www.typescriptlang.org/docs/handbook/release-notes/typescript-4-5.html#tail-recursion-elimination-on-conditional-types tail recursive conditional type} approach to deal with performance issues for very long paths
 *
 * @example
 * ```
 * type Segments = PathSegments<'/courses/:courseId/classes/:classId'>;
 * //   ^? "" | "courses" | ":courseId" | "classes" | ":classId"
 * ```
 */
type PathSegments<
  Path extends string,
  Accumulator extends string = never,
> = Path extends `${infer Left}/${infer Right}`
  ? PathSegments<Right, Accumulator | Left>
  : Accumulator | Path;

/**
 * Creates an object having
 * keys = filtered PathSegments that starts with `:` and
 * value = string
 *
 * Uses {@link https://www.typescriptlang.org/docs/handbook/2/mapped-types.html#key-remapping-via-as key remapping} to suppress any union members that don't begin with `:`
 *
 * @example 1 - when there are no dynamic params in Path
 * ```
 * type Params = RouteParams<'/courses/classes'>;
 * //   ^? {}
 * ```
 *
 * @example 2 - when there are dynamic params in Path
 * ```
 * type Params = RouteParams<'/courses/:courseId/classes/:classId'>;
 * //   ^? { courseId: string; classId: string }
 * ```
 */
export type RouteParams<Path extends string> = {
  [PathSegment in PathSegments<Path> as PathSegment extends `:${infer RouteParam}`
    ? ExtractEnum<RouteParam> extends never
      ? RouteParam
      : RouteParam extends `${infer ActualParam}(${string})`
        ? ActualParam
        : never
    : never]: PathSegment extends `:${infer RouteParam}`
    ? ExtractEnum<RouteParam> extends never
      ? string
      : ParseEnumValues<ExtractEnum<RouteParam>>
    : never;
};

type Params =
  RouteParams<`:universitySlug/courses/:courseId/classes/:classId/polls/new/:toBeDone(${ToBeDone.IN_CLASS}|${ToBeDone.PRE_CLASS})`>;

// type RouteUrl<
//   Path extends string,
//   Params extends RouteParams<Path>,
// > = Path extends `${infer Left}/:${infer Param}/${infer Right}`
//   ? // @ts-ignore // FIXME: fix type issue
//     `${Left}/${Params[Param]}/${RouteUrl<Right, Params extends RouteParams<Right> ? Params : never>}`
//   : Path extends `${infer Left}/:${infer Param}`
//     ? // @ts-ignore // FIXME: fix type issue
//       `${Left}/${Params[Param]}`
//     : Path;

type RemoveEnumValues<Param extends string> = Param extends `${infer Before}(${infer EnumValues})`
  ? Before
  : never;

type X = RemoveEnumValues<`toBeDone(${ToBeDone.IN_CLASS}|${ToBeDone.PRE_CLASS})`>;

type RouteUrl<
  Path extends string,
  Params extends RouteParams<Path>,
> = Path extends `:${infer Param}/${infer Right}`
  ? RemoveEnumValues<Param> extends never
    ? // @ts-ignore // FIXME: fix type issue
      `${Params[Param]}/${RouteUrl<Right, Params extends RouteParams<Right> ? Params : never>}`
    : // @ts-ignore // FIXME: fix type issue
      `${Params[RemoveEnumValues<Param>]}/${RouteUrl<Right, Params extends RouteParams<Right> ? Params : never>}`
  : Path extends `${infer Left}/:${infer Param}/${infer Right}`
    ? // @ts-ignore // FIXME: fix type issue
      RemoveEnumValues<Param> extends never
      ? // @ts-ignore // FIXME: fix type issue
        `${Left}/${Params[Param]}/${RouteUrl<Right, Params extends RouteParams<Right> ? Params : never>}`
      : // @ts-ignore // FIXME: fix type issue
        `${Left}/${Params[RemoveEnumValues<Param>]}/${RouteUrl<
          Right,
          Params extends RouteParams<Right> ? Params : never
        >}`
    : Path extends `${infer Left}/:${infer Param}`
      ? // @ts-ignore // FIXME: fix type issue
        RemoveEnumValues<Param> extends never
        ? // @ts-ignore // FIXME: fix type issue
          `${Left}/${Params[Param]}`
        : // @ts-ignore // FIXME: fix type issue
          `${Left}/${Params[RemoveEnumValues<Param>]}`
      : Path;

type Url = RouteUrl<'/courses/:courseId/classes/:classId', { courseId: '123'; classId: '456' }>;
type Url2 = RouteUrl<
  `/courses/:courseId/classes/:classId/:toBeDone(${ToBeDone.PRE_CLASS}|${ToBeDone.IN_CLASS})/test`,
  { courseId: '123'; classId: '456'; toBeDone: ToBeDone.IN_CLASS }
>;

export function extractParams<Path extends string>(path: Path, url: string) {
  const matchedPath = matchPath(path, url);
  if (!matchedPath) return null;

  return matchedPath.params as unknown as RouteParams<Path>;
}

export const createRouteUrl =
  <Path extends string>(path: Path) =>
  (
    ...params: RouteParams<Path> extends EmptyObject ? [undefined?] : [RouteParams<Path>]
  ): RouteUrl<Path, RouteParams<Path>> => {
    const routeParams = params[0];
    if (!routeParams) return path as RouteUrl<Path, RouteParams<Path>>;

    let url = path;

    const paramsKeys = Object.keys(routeParams) as (keyof typeof routeParams)[];

    paramsKeys.forEach((key) => {
      // @ts-ignore // FIXME: fix type issue
      // replace params with and without enum values
      url = url.replace(/:[A-Za-z0-9(|_)]{1,}/, routeParams[key]);
    });

    return url as RouteUrl<Path, RouteParams<Path>>;
  };

interface CreateRouteParams<
  Path extends string,
  AcadlyWebPath extends string = Path,
  DependsOn extends CreateRouteParams<any> = never,
> {
  path: Path;
  acadlyWebPath?: AcadlyWebPath;
  dependsOn?: DependsOn;
}

export function createRoute<
  Path extends string,
  AcadlyWebPath extends string = Path,
  DependsOn extends CreateRouteParams<any> = never,
>({
  path,
  dependsOn,
}: CreateRouteParams<Path, AcadlyWebPath, DependsOn>): [DependsOn] extends [never]
  ? {
      path: Path;
      relativePath: Path;
      url: ReturnType<typeof createRouteUrl<Path>>;
      acadlyWebPath: AcadlyWebPath;
      relativeAcadlyWebPath: AcadlyWebPath;
      acadlyWebUrl: ReturnType<typeof createRouteUrl<AcadlyWebPath>>;
      parent: null;
    }
  : {
      path: DependsOn['path'] extends '/' ? `/${Path}` : `${DependsOn['path']}/${Path}`;
      relativePath: Path;
      url: ReturnType<
        typeof createRouteUrl<DependsOn['path'] extends '/' ? `/${Path}` : `${DependsOn['path']}/${Path}`>
      >;
      acadlyWebPath: DependsOn['acadlyWebPath'] extends '/'
        ? `/${AcadlyWebPath}`
        : `${DependsOn['acadlyWebPath']}/${AcadlyWebPath}`;
      relativeAcadlyWebPath: AcadlyWebPath;
      acadlyWebUrl: ReturnType<
        typeof createRouteUrl<
          DependsOn['acadlyWebPath'] extends '/'
            ? `/${AcadlyWebPath}`
            : `${DependsOn['acadlyWebPath']}/${AcadlyWebPath}`
        >
      >;
      parent: DependsOn;
    };
export function createRoute({
  path: _relativePath,
  acadlyWebPath: _relativeAcadlyWebPath = '',
  dependsOn,
}: CreateRouteParams<any, any, any>) {
  const relativePath = _relativePath.replace(/\([A-Za-z0-9|_]{1,}\)/gm, '');
  const relativeAcadlyWebPath = _relativeAcadlyWebPath
    ? _relativeAcadlyWebPath.replace(/\([A-Za-z0-9|_]{1,}\)/gm, '')
    : relativePath;

  if (!dependsOn) {
    return {
      path: relativePath,
      relativePath,
      url: createRouteUrl(relativePath),
      acadlyWebPath: relativeAcadlyWebPath,
      relativeAcadlyWebPath,
      acadlyWebUrl: createRouteUrl(relativeAcadlyWebPath),
      parent: null,
    };
  }

  const parent = createRoute(dependsOn);
  // avoid 2 sequential slashes at the beginning of path
  if (parent.path === '/') {
    return {
      path: `${parent.path}${relativePath}`,
      relativePath,
      url: createRouteUrl(`${parent.path}${relativePath}`),
      acadlyWebPath: `${parent.acadlyWebPath ?? parent.path}${relativeAcadlyWebPath}`,
      relativeAcadlyWebPath,
      acadlyWebUrl: createRouteUrl(`${parent.acadlyWebPath ?? parent.path}${relativeAcadlyWebPath}`),
      parent: dependsOn,
    };
  }

  return {
    path: `${parent.path}/${relativePath}`,
    relativePath,
    url: createRouteUrl(`${parent.path}/${relativePath}`),
    acadlyWebPath: `${parent.acadlyWebPath ?? parent.path}/${relativeAcadlyWebPath}`,
    relativeAcadlyWebPath,
    acadlyWebUrl: createRouteUrl(`${parent.acadlyWebPath ?? parent.path}/${relativeAcadlyWebPath}`),
    parent: dependsOn,
  };
}
